import Link from "next/link";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import {
  ADDON_SUBSCRIPTIONS,
  BRAND_PATHS,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import {
  selectDeviationProducts,
  useBillingQuery,
  usePickAndMixQuery,
} from "@chef/state-management";
import { PageLoader, ShareButton, ShoppingCart } from "@chef/smart-components";
import {
  formatTimeInterval,
  formatDate,
  padPostalCode,
  getNextCutoffs,
  getWeekYearSortableNumber,
  isMealboxBasketType,
} from "@chef/helpers";
import { getBasketDataFromCalendar } from "@chef/state-management/helpers";
import { AppShell } from "@chef/feature-app-shell";
import { isEqualStrings } from "@chef/utils/equal";
import { Seedling } from "@chef/icons/large";
import { Button, Card } from "@chef/components";
import {
  useCalendarData,
  useOrderDetails,
  useSharedMenuUrl,
} from "@chef/state-management/hooks";
import { Loading, Truck } from "@chef/icons/small";
import type { WeekYear } from "@chef/types/common";

import { useCreateDeviationBasket } from "../hooks";

import { intl, brand } from "./WeekEditorSuccessExperimental.Intl";

export const WeekEditorSuccessExperimental = ({ week, year }: WeekYear) => {
  const router = useRouter();
  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const { data: billingQuery } = useBillingQuery();

  const [createSharedMenuUrl, { isLoading: isCreatingShortLink }] =
    useSharedMenuUrl();

  // queries
  // NOTE: this will re-fetch the new calendar
  const { isLoading: isLoadingCreateDeviationBasket } =
    useCreateDeviationBasket({ week, year });

  const {
    calendar,
    isLoading: isLoadingCalendar,
    isFetching: isFetchingCalendar,
  } = useCalendarData({
    week,
    year,
  });

  const calendarData = calendar?.filter(
    (c) => c.week === week && c.year === year,
  );

  const { data: pickAndMixData, isLoading: isLoadingPickAndMix } =
    usePickAndMixQuery({
      productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
      week,
      year,
    });

  const firstCalendarWeek = calendarData?.find(() => true);

  const basket = firstCalendarWeek?.baskets.find(
    (basket) =>
      basket.week === week &&
      basket.year === year &&
      isMealboxBasketType(basket), // we can pass just mealbox basket because data from hook used for shared order
  );

  const { data: currentOrderData, isReady } = useOrderDetails({
    type: "deviation",
    mainObject: basket,
    pickAndMix: pickAndMixData?.pickAndMix,
  });

  if (!calendarData || !pickAndMixData) {
    return <PageLoader />;
  }

  const basketData = getBasketDataFromCalendar({
    calendar: calendarData,
    week,
    year,
  });

  // misc
  const isLoading =
    router.isFallback ||
    isLoadingCreateDeviationBasket ||
    isLoadingPickAndMix ||
    isLoadingCalendar;

  // selectedProducts might be null until the next tick -- can be a race condition
  if (isLoading || !selectedProducts || !basketData) {
    return <PageLoader />;
  }

  const nextCutoffs = getNextCutoffs(3);

  const nextCutoff = nextCutoffs.find(
    (cutoff) =>
      getWeekYearSortableNumber(cutoff) >
      getWeekYearSortableNumber({ week, year }),
  );

  const {
    shippingDetails: {
      plannedDeliveryInformation: {
        deliveryDate = "",
        to = "",
        from = "",
      } = {},
      addressInfo: { addressId = "" } = {},
    } = {},
  } = basketData.mealboxBasket || {};

  const deliveryDateString = formatDate(new Date(deliveryDate), undefined, {
    lowerCase: true,
  });

  const deliveryAddress = billingQuery?.billing.addresses.find(
    (address) => address.addressId === addressId,
  );

  const fullDeliveryAddressString = `${
    deliveryAddress?.address
  }, ${padPostalCode(deliveryAddress?.postalCode || "")} ${
    deliveryAddress?.city
  }`;

  const isOnlyDigitalRecipes = billingQuery?.billing.addonSubscriptions.find(
    (s) =>
      isEqualStrings(s.subscriptionId, ADDON_SUBSCRIPTIONS.DIGITAL_RECIPES),
  )?.active;

  const handleCreateLink = async () => {
    if (!currentOrderData || !isReady) {
      throw new Error("Missing data or not ready");
    }

    return createSharedMenuUrl(currentOrderData);
  };

  return (
    <AppShell title={brand.TITLE}>
      <AppShell.Meta />

      <AppShell.Drawer />

      <AppShell.Sticky anchor="top" float>
        <AppShell.Header variant="my-menu" />
      </AppShell.Sticky>

      <AppShell.Main grid>
        <div className="col-span-4 text-center md:col-span-6 md:col-start-2 xl:col-start-4">
          <div className="relative flex items-center md:justify-center">
            <h1 className="text-left md:text-center md:w-full">
              <strong>{brand.TITLE}</strong>
            </h1>
            <ShareButton
              variant="descriptive"
              onClick={handleCreateLink}
              isLoading={isCreatingShortLink}
              className="ml-auto md:absolute md:right-0 outline-2 outline-primary outline focus:outline-2 focus:outline-primary"
            />
          </div>

          <Card className="mt-6">
            <div className="flex items-center gap-4">
              <Truck className="w-16 h-16 shrink-0" />
              <p className="text-left">
                {intl.YOUR_ORDER_WILL_BE_DELIVERED}{" "}
                <strong>
                  {deliveryDateString}, {formatTimeInterval(from, to, "m")}.
                </strong>
                <br />
                <strong>{fullDeliveryAddressString}</strong>
              </p>
            </div>

            <hr className="my-3 border-grey-2" />

            <p className="mt-4 text-sm">
              {intl.QUESTIONS}{" "}
              <Link href={BRAND_PATHS.CONTACT_US_PAGE} className="underline">
                {intl.CONTACT_CUSTOMER_SERVICE_HERE}
              </Link>
            </p>
          </Card>

          <Card className="mt-4">
            <h2 className="text-xl text-left">
              <strong>{intl.RECEIPT}</strong>
            </h2>

            {firstCalendarWeek && (
              <div className="relative text-left">
                {isFetchingCalendar && (
                  <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50">
                    <Loading className="w-8 h-8 animate-spin" />
                  </div>
                )}
                <ShoppingCart
                  calendarWeek={firstCalendarWeek}
                  initiallyExpandMealSection
                />
              </div>
            )}
          </Card>

          {isOnlyDigitalRecipes && (
            <div className="mt-4">
              <Seedling />
              <span className="ml-2">
                {
                  intl.WITHOUT_RECIPE_SHEET_THANKS_FOR_THINKING_ABOUT_THE_ENVIRONMENT
                }
              </span>
            </div>
          )}

          <div className="flex flex-col items-stretch justify-center gap-4 mt-6 lg:flex-row">
            <Link href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}>
              <Button
                data-intercom-target="back-to-weekly"
                id="back-to-overview-btn"
                full
                outlined
              >
                {intl.BACK_TO_OVERVIEW}
              </Button>
            </Link>
            {nextCutoff && (
              <Link
                href={BRAND_PATHS.WEEK_EDITOR_PAGE({
                  week: nextCutoff.week,
                  year: nextCutoff.year,
                })}
              >
                <Button primary id="pick-dishes-for-next-week-btn" full>
                  {intl.PICK_DISHES_FOR_NEXT_WEEK_BUTTON(nextCutoff.week)}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </AppShell.Main>

      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar variant="my-menu" />
      </AppShell.Sticky>
    </AppShell>
  );
};
